import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { Env } from '@ctw/shared/utils/environments';
import type { AnyJsonObj } from '@ctw/shared/utils/types';
import { getUnleashProxyEnvironmentConfig } from '@ctw/shared/utils/unleash';
import { useQueryClient } from '@tanstack/react-query';
import { FlagProvider, type IStorageProvider, UnleashClient } from '@unleash/proxy-client-react';
import { omit } from 'lodash-es';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type ZodType, z } from 'zod';
import { LoadingSpinner } from '../components/loading-spinner';

/**
 * Storage provider that uses localStorage when available (normal browser context)
 * and falls back to in-memory storage when it's not (e.g. sandboxed iframes,
 * private browsing restrictions, or environments without window.localStorage).
 */
class ResilientLocalStorageProvider implements IStorageProvider {
  private memoryStore: Record<string, unknown> = {};

  private isLocalStorageAvailable(): boolean {
    try {
      const testKey = '__unleash_storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  public save(name: string, data: unknown): Promise<void> {
    if (this.isLocalStorageAvailable()) {
      try {
        window.localStorage.setItem(name, JSON.stringify(data));
        return Promise.resolve();
      } catch {
        /* fall through to memory store */
      }
    }
    this.memoryStore[name] = data;
    return Promise.resolve();
  }

  public get(name: string): Promise<unknown> {
    if (this.isLocalStorageAvailable()) {
      try {
        const value = window.localStorage.getItem(name);
        return Promise.resolve(value ? JSON.parse(value) : undefined);
      } catch {
        /* fall through to memory store */
      }
    }
    return Promise.resolve(this.memoryStore[name]);
  }
}

export interface UnleashFeatureOverrides {
  flags?: Record<string, ReturnType<typeof useFeatureFlag>>;
  variants?: Record<string, ReturnType<typeof useFeatureVariant>>;
}

type FeatureFlags = {
  client: UnleashClient;
  flags: Record<string, boolean>;
  unleashFeatureOverrides: UnleashFeatureOverrides;
};

export const FeatureFlagContext = createContext<FeatureFlags | undefined>(undefined);

interface FeatureFlagProviderProps extends PropsWithChildren {
  env: Env;
  unleashFeatureOverrides: UnleashFeatureOverrides;
}

export function FeatureFlagProvider({
  env,
  unleashFeatureOverrides,
  children,
}: FeatureFlagProviderProps) {
  const { requestContext } = useCTW();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>();
  const queryClient = useQueryClient();
  const startedRef = useRef(false);

  const storageProvider = useMemo(() => new ResilientLocalStorageProvider(), []);

  const unleashClient = useMemo(
    () =>
      new UnleashClient({
        ...getUnleashProxyEnvironmentConfig(env),
        refreshInterval: 15, // 15 seconds
        appName: 'component-library',
        storageProvider,
        context: {
          userId: requestContext.authTokenState.zusUserId,
          properties: {
            builderId: requestContext.builderId,
            builderName: requestContext.authTokenState.builderName,
            userType: requestContext.authTokenState.userType,
            email: requestContext.authTokenState.email,
            /* For some reason unleash proxy does not like localhost appearing
             * in the query params and will return 403's.
             * As a work around, we replace localhost with local.
             */
            hostname: window.location.hostname.replace('localhost', 'local'),
            ehr: requestContext.ehr,
          },
        },
      }),
    [
      env,
      storageProvider,
      requestContext.builderId,
      requestContext.authTokenState.builderName,
      requestContext.authTokenState.email,
      requestContext.authTokenState.userType,
      requestContext.authTokenState.zusUserId,
      requestContext.ehr,
    ],
  );

  const buildFeatureFlags = (client: UnleashClient): FeatureFlags => ({
    client,
    flags: Object.fromEntries(
      client.getAllToggles().map((toggle) => [toggle.name, toggle.enabled]),
    ),
    unleashFeatureOverrides,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: We trigger startUnleashClient whenever any of the relevant auth or feature flag variables change. This ensures the on.ready callback contains all proper references when it finally initializes.
  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const startUnleashClient = async () => {
      // Wait for init() to complete — this loads cached flags from localStorage
      // (via the storageProvider) without making a network request.
      // @ts-expect-error - `ready` is a public Promise on UnleashClient but not in the type defs
      await unleashClient.ready;

      // If cached flags exist from a previous session, render immediately with them.
      // This avoids blocking on the network fetch for returning users.
      const cachedToggles = unleashClient.getAllToggles();
      if (cachedToggles.length > 0) {
        setFeatureFlags(buildFeatureFlags(unleashClient));
      }

      // Listen for network fetch completion to update with fresh flags
      unleashClient.on('ready', () => {
        setFeatureFlags(buildFeatureFlags(unleashClient));
      });

      // Also listen for subsequent updates (from refresh interval)
      unleashClient.on('update', () => {
        setFeatureFlags(buildFeatureFlags(unleashClient));
      });

      // Start the client — triggers the first network fetch and sets up
      // the 15-second refresh interval
      await unleashClient.start();
    };

    void startUnleashClient();
  }, [
    requestContext.authToken,
    unleashClient,
    unleashFeatureOverrides,
    requestContext.authTokenState.zusUserId,
    requestContext.authTokenState.builderId,
    requestContext.authTokenState.builderName,
    requestContext.authTokenState.userType,
    requestContext.authTokenState.email,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Using queryClient would invalidate queries too often. We only want to invalidate queries when the feature flags change.
  useEffect(() => {
    void queryClient.invalidateQueries();
  }, [featureFlags]);

  if (featureFlags === undefined) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  return (
    <FlagProvider unleashClient={unleashClient} startClient={false}>
      <FeatureFlagContext.Provider value={featureFlags}>{children}</FeatureFlagContext.Provider>
    </FlagProvider>
  );
}

export function useFeatureFlag(name: string): boolean {
  const featureFlags = useContext(FeatureFlagContext);

  if (featureFlags === undefined) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
  }

  if (featureFlags.unleashFeatureOverrides.flags?.[name] !== undefined) {
    return featureFlags.unleashFeatureOverrides.flags[name];
  }

  return featureFlags.flags[name] ?? false;
}

export function useHasFeatureFlag(): (name: string) => boolean {
  const featureFlags = useContext(FeatureFlagContext);

  if (featureFlags === undefined) {
    throw new Error('useHasFeatureFlag must be used within a FeatureFlagProvider');
  }

  return (name: string) => {
    if (featureFlags.unleashFeatureOverrides.flags?.[name] !== undefined) {
      return featureFlags.unleashFeatureOverrides.flags[name];
    }

    return featureFlags.flags[name] ?? false;
  };
}

export const ZAP_ALERT_SCHEMA = z.object({
  type: z.enum(['caution', 'info']),
  message: z.string(),
  link: z.string().optional(),
});

const VARIANT_USER_TARGETED_FEATURE_VERSION_SCHEMA = z.object({
  featureVersionId: z.string(),
  targeting: z.object({
    emails: z.array(z.string()).optional(),
    builderIds: z.array(z.string()).optional(),
  }),
  payload: z.record(z.string(), z.unknown()).optional(),
});
const VARIANT_USER_TARGETED_SCHEMA = z.object({
  variantType: z.enum(['userTargeted']),
  featureVersions: z.array(VARIANT_USER_TARGETED_FEATURE_VERSION_SCHEMA),
});

const featureFlagNamesWithScalarVariants = ['foop', 'doop'] as const;
type FeatureFlagNameWithScalarVariants = (typeof featureFlagNamesWithScalarVariants)[number];
const isFeatureFlagNameWithScalarVariants = (
  name: string,
): name is FeatureFlagNameWithScalarVariants =>
  featureFlagNamesWithScalarVariants.includes(name as FeatureFlagNameWithScalarVariants);

const featureFlagNamesWithUserTargetedJsonVariant = ['ctw-test-variant'] as const;

const featureFlagNamesWithJsonVariants = [
  'ctw-zap-alert',
  'ctw-standalone-alert',
  'ctw-test-variant',
] as const;
type FeatureFlagNameWithUserTargetedJsonVariant =
  (typeof featureFlagNamesWithUserTargetedJsonVariant)[number];
const isFeatureFlagNameWithJsonVariants = (name: string): name is FeatureFlagNameWithJsonVariants =>
  featureFlagNamesWithJsonVariants.includes(name as FeatureFlagNameWithJsonVariants);

type FeatureFlagNameWithJsonVariants = (typeof featureFlagNamesWithJsonVariants)[number];

const isFeatureFlagNameWithUserTargetedJsonVariant = (
  name: string,
): name is FeatureFlagNameWithUserTargetedJsonVariant =>
  featureFlagNamesWithUserTargetedJsonVariant.includes(
    name as FeatureFlagNameWithUserTargetedJsonVariant,
  );
const isUserTargetedFeatureVariantPayload = (
  variantPayload: AnyJsonObj,
): variantPayload is z.infer<typeof VARIANT_USER_TARGETED_SCHEMA> =>
  variantPayload.variantType === 'userTargeted';

type FeatureFlagNameWithVariants =
  | FeatureFlagNameWithJsonVariants
  | FeatureFlagNameWithUserTargetedJsonVariant
  | FeatureFlagNameWithScalarVariants;

const featureFlagVariantPayloadSchemas = {
  'ctw-zap-alert': ZAP_ALERT_SCHEMA,
  'ctw-standalone-alert': ZAP_ALERT_SCHEMA,
  'ctw-test-variant': VARIANT_USER_TARGETED_SCHEMA,
} as const satisfies Record<FeatureFlagNameWithJsonVariants, ZodType>;

type FeatureFlagVariantJsonPayloads = {
  [key in FeatureFlagNameWithJsonVariants]: z.infer<(typeof featureFlagVariantPayloadSchemas)[key]>;
};

type FeatureFlagVariantScalarPayloads = {
  foop: string;
  doop: number;
};

type FeatureFlagVariantPayloads = FeatureFlagVariantJsonPayloads & FeatureFlagVariantScalarPayloads;

interface Variant<TFeatureFlagName extends FeatureFlagNameWithVariants> {
  name: string;
  payload: TFeatureFlagName extends FeatureFlagNameWithUserTargetedJsonVariant
    ? z.infer<typeof VARIANT_USER_TARGETED_FEATURE_VERSION_SCHEMA>['payload']
    : FeatureFlagVariantPayloads[TFeatureFlagName];
}

export const useFeatureVariant = <TFeatureFlagName extends FeatureFlagNameWithVariants>(
  name: TFeatureFlagName,
): Variant<TFeatureFlagName> | undefined => {
  const { requestContext } = useCTW();
  const featureFlags = useContext(FeatureFlagContext);
  const telemetry = useTelemetry();

  if (featureFlags === undefined) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
  }

  const variant = featureFlags.client.getVariant(name);

  if (!(variant.enabled && variant.payload)) {
    return undefined;
  }

  if (isFeatureFlagNameWithScalarVariants(name)) {
    if (variant.payload.type === 'number' && !Number.isNaN(Number(variant.payload.value))) {
      return {
        name: variant.name,
        payload: Number(variant.payload.value) as Variant<TFeatureFlagName>['payload'],
      };
    }

    if (variant.payload.type === 'string' && typeof variant.payload.value === 'string') {
      return {
        name: variant.name,
        payload: Number(variant.payload.value) as Variant<TFeatureFlagName>['payload'],
      };
    }

    telemetry.logger.error(
      `Unexpected variant payload of type "${typeof variant.payload.value}" for variant "${variant.payload.type}" of feature "${name}"`,
    );
    return undefined;
  }

  if (
    isFeatureFlagNameWithJsonVariants(name) ||
    isFeatureFlagNameWithUserTargetedJsonVariant(name)
  ) {
    const variantSchema = Object.keys(featureFlagVariantPayloadSchemas).includes(name)
      ? featureFlagVariantPayloadSchemas[name]
      : undefined;
    if (!variantSchema) {
      telemetry.logger.warn(`Missing variant schema for feature "${name}"`);
      return undefined;
    }

    const result = variantSchema.safeParse(JSON.parse(variant.payload.value));
    if (!result.success) {
      telemetry.logger.error(
        `Failed to parse variant "${variant.name}" for feature "${name}", ${result.error.toString()}`,
      );
      return undefined;
    }

    const variantPayload = result.data;

    if (
      isFeatureFlagNameWithUserTargetedJsonVariant(name) &&
      isUserTargetedFeatureVariantPayload(variantPayload)
    ) {
      const targetedFeatureVersion = variantPayload.featureVersions.find(
        (featureVersion: z.infer<typeof VARIANT_USER_TARGETED_FEATURE_VERSION_SCHEMA>) =>
          featureVersion.targeting.emails?.includes(requestContext.authTokenState.email) ||
          featureVersion.targeting.builderIds?.includes(requestContext.authTokenState.builderId),
      );

      if (!targetedFeatureVersion) {
        return undefined;
      }

      // TypeScript can't narrow generic type parameters through control flow,
      // so we need to assert the payload type. We know this is safe because
      // we've checked isFeatureFlagNameWithUserTargetedJsonVariant(name)
      return {
        name: targetedFeatureVersion.featureVersionId,
        payload: omit(targetedFeatureVersion.payload, [
          'targeting',
        ]) as Variant<TFeatureFlagName>['payload'],
      };
    }

    return {
      name: variant.name,
      payload: variantPayload as Variant<TFeatureFlagName>['payload'],
    };
  }

  telemetry.logger.error(
    `Feature "${name}" returned an unexpected variant type "${variant.payload.type}"`,
  );
  return undefined;
};
